import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import { automationErrorLogCodeOptions, automationErrorLogCodes, automationErrorLogStatusOptions } from 'constants/automationConstants';
import { listAutomationErrorLogs } from 'services/Settings';

import { PrimeReactConfirmDialog, PrimeReactDataTable, PrimeReactDialog } from 'components/common';
import { generateAutomationErrorLogDataTableColumns, initialFilterValues } from './configs/datatable_configs';
import { errorFixDialogFormInitialValues } from './configs/dialog_configs';
import {
  automationErrorLogApiServices,
  dialogHelpers,
  generateFixErrorConfirmDialogMessage,
  generateGetAutomationErrorLogsApiPayload,
  handlePagination,
  getActionLabelsList,
} from './helpers/automationErrorLogHelpers';
import {
  AutomationErrorLogFilters,
  DynamicErrorLogForm,
  AutomationErrorLogHeading,
  AutomationErrorLogDetails,
} from './facets/AutomationErrorLogFacets';
import { AutomationContextProvider, useAutomationContext } from '../../features/context/WorkflowAutomationContext';
import useAxiosLoader from 'hooks/useAxiosLoader';
import { automationErroLogLocalStorageItemsList, updateAutomationRuleLocalStorageItemsList } from '../../configs/localStorageConfigs';
import { clearLocalStorageItems } from 'utils/localStorageUtils';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { handlePageNumberInPagination } from 'common/pagination';
import { UserDetailsContext } from 'context/userDetailsContext';
import { generateFullAttendeesListReverse } from '../../submodules/events/helpers/createEventHelpers';
import { getWorkFlowAutomationRuleDetails } from 'services/workflowAutomation/workflowAutomationRuleServices';
import { generateFullMessageRecipientsListReverse, getMessageSendersList } from '../../submodules/messages/helpers/createMessageHelpers';
import { getTenantDetails, setAppTitle } from 'utils/utils';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { AppConfig } from 'config/api_config';
import useEncryptedURLState from 'hooks/useEncryptedURLState';

function ListAutomationErrorLog(props) {
  const userDetails = useContext(UserDetailsContext);
  const history = useHistory();
  const { automationErrorLogParentId } = useParams();
  const [tenantId] = getTenantDetails();
  const selectedSk = `automation_rule#${automationErrorLogParentId}`;
  const selectedPk = `${tenantId}#${automationErrorLogParentId}`;

  const {
    addToast,
    userContext,
    toastConstant,
    tenantSk,
    constants,
    generateFullName,
    totalCountEvent,
    firmUsersList,
    getAllRequiredContactsList,
    triggerName,
    dispatch,
  } = useAutomationContext() || {};

  const { customHeaderTemplate, renderFooter } = dialogHelpers || {};

  const [loading] = useAxiosLoader() || {};

  const {
    userNotInCase,
    noLawftAccess,
    noEmail,
    noBillingContact,
    noPhoneNumber,
    eventTimePassed,
    noLeadAttorney,
    noLeadAssociate,
    noLeadParalegal,
    noLeadCaseManager,
    userArchived,
    userUnconfirmed,
    senderArchived,
    senderUnconfirmed,
    groupMembersArchived,
    groupMembersUnconfirmed,
    noBusinessEmail,
    noLeadUser,
    noLawftId,
  } = automationErrorLogCodes || {};

  const { month_date_year_format, month_date_year_time_12_format, last_updated_format } = constants || {};

  const {
    getBillingContactsList,
    getContactDetails,
    getTeamMembersList,
    handleUserNotInCaseError,
    handleUpdateErrorOccuredContactDetails,
    handleNoBillingContactError,
    handleNoLeadTeamMemberError,
    handleUsersAutomationErrors,
    handleSenderAutomationErrors,
    handleBusinessEmailAutomationErrors,
    handleLeadUserAutomationErrors,
    handleLawftIdAutomationErrors,
  } = automationErrorLogApiServices || {};

  const [errorFixDialogData, setErrorFixDialogData] = useState({
    show: false,
  });

  const [showFixErrorConfirmDialog, setShowFixErrorConfirmDialog] = useState(false);

  const [filterValues, setFilterValues] = useEncryptedURLState('filter', initialFilterValues);
  const [automationErrorLogsList, setAutomationErrorLogsList] = useState([]);
  const [automationErrorLogsDetails, setAutomationErrorLogsDetails] = useState();

  const [selectedAutomationRuleDetails, setSelectedAutomationRuleDetails] = useState();
  const [selectedAutomationErrorLogDetails, setSelectedAutomationErrorLogDetails] = useState();

  //pagination related
  let initalPaginationVal = {
    pageLimit: userDetails?.firmDetails?.pagination_preference?.automation?.error_list ?? 10,
    apiTransitionToken: '',
    apiNextToken: '',
    isForward: true,
  };
  const [paginationData, setPaginationData] = useState(initalPaginationVal);

  //billing contact state
  const [billingContactsOptions, setBillingContactsOptions] = useState([]);

  //teamMembersList - contains all case-specific team members and other firm users
  const [teamMembersList, setTeamMembersList] = useState({
    existingTeamMembersWithRoles: [],
    availableTeamMembersWithoutRoles: [],
  });

  const [fullAttendeesList, setFullAttendeesList] = useState([]);

  const [messageRecipientsList, setMessageRecipientsList] = useState([]);

  //This ref is used to fix "firmUsersList" array becoming empty
  let firmUserRef = useRef('');

  //contact details state
  const [contactData, setContactData] = useState();
  const [totalCount, setTotalCount] = useState(0);

  //============================== react hook form section starts =======================
  const reactHookForm = useForm({
    defaultValues: errorFixDialogFormInitialValues,
  });
  const { getValues, reset: resetReactHookForm } = reactHookForm || {};

  //============================ react hook form section ends ==================

  /** function to calculate total count of automation-error-log-list */

  const loadTotalCount = () => {
    let moduleName = 'automation_error';
    totalCountEvent(moduleName, '', '', '', selectedPk)
      .then((response) => {
        setTotalCount(response?.data);
      })
      .catch((err) => {
        console.log('totalcount-err', err);
      });
  };

  const getAutomationErrorLogs = async (sk = '', data = {}, pagination = {}) => {
    try {
      const {
        pageLimit = userDetails?.firmDetails?.pagination_preference?.automation?.error_list ?? 10,
        token = '',
        direction = 'forward',
        isForward = true,
      } = pagination || {};

      let response = await listAutomationErrorLogs(sk, data, pageLimit, token, direction);

      if (response?.data) {
        const { automation_error_logs = [], next_token = '', transition_token = '' } = response?.data || {};

        if (automation_error_logs?.length > 0) {
          const selectedWorkflowTitle = automation_error_logs[0]?.automation_title || 'N/A';
          const selectedTriggerName = (automation_error_logs[0]?.fire_on && automation_error_logs[0]?.fire_on['label']) || 'N/A';
          const selectedActionNames = getActionLabelsList(automation_error_logs);
          setAutomationErrorLogsDetails({
            selectedWorkflowTitle,
            selectedTriggerName,
            selectedActionNames,
          });
        }

        setAutomationErrorLogsList([...automation_error_logs]);

        setPaginationData({
          pageLimit,
          isForward,
          apiNextToken: next_token,
          apiTransitionToken: transition_token,
        });
      }
    } catch (err) {
      clearLocalStorageItems(automationErroLogLocalStorageItemsList);

      console.log('err', err);
    }
  };

  const getHandlePaginationCommonParms = (e) => {
    return {
      paginationData,
      filterValues,
      selectedSk,
      last_updated_format,
      getAutomationErrorLogs,
    };
  };

  const memoHandleFilterValueChange = useCallback(
    (event) => {
      let { name, value } = event?.target || {};
      let makeApiRequest = true;
      let payloadData = {};
      let filterValuesObj = {};

      if (name === 'status' && value === 'showAll') value = '';

      filterValuesObj = {
        ...filterValues,
        [name]: value,
      };

      payloadData = generateGetAutomationErrorLogsApiPayload(filterValuesObj, last_updated_format);

      if (payloadData?.automation_error_start_date && !payloadData?.automation_error_end_date) {
        makeApiRequest = false;
      }

      setFilterValues(filterValuesObj);
      if (makeApiRequest) getAutomationErrorLogs(selectedSk, payloadData);
    },
    [filterValues]
  );

  const resetFilters = () => {
    getAutomationErrorLogs(selectedSk);
    setFilterValues(initialFilterValues);
  };

  /** --- handleFixActionButtonClick function is triggered when we click on the "fix" button in the error log datatable --- **/
  const handleFixActionButtonClick = (rowData = {}) => {
    setSelectedAutomationErrorLogDetails(rowData);
    const { error_code } = rowData;

    if (error_code === noBillingContact || error_code === noLawftId) {
      getBillingContactsList({
        tenantId,
        tenantSk,
        generateFullName,
        rowData,
        setBillingContactsOptions,
      });
    } else if (error_code === noLawftAccess) {
      getContactDetails({ setContactData, rowData });
    } else if ([noLeadAttorney, noLeadAssociate, noLeadParalegal, noLeadCaseManager]?.includes(error_code)) {
      getTeamMembersList({
        tenantId,
        tenantSk,
        generateFullName,
        rowData,
        setTeamMembersList,
        firmUsersArr: firmUserRef?.current,
        error_code,
      });
    } else if ([userArchived, userUnconfirmed, groupMembersArchived, groupMembersUnconfirmed].includes(error_code)) {
      if (firmUserRef?.current?.length) {
        generateFullAttendeesListReverse({
          setFullAttendeesList,
          firmUsersList: firmUserRef?.current,
          getAllRequiredContactsList,
        });
      }
    } else if ([senderArchived, senderUnconfirmed].includes(error_code)) {
      if (firmUsersList?.length) {
        generateFullMessageRecipientsListReverse({
          setMessageRecipientsList,
          firmUsersList,
          getAllRequiredContactsList,
        });
      }
    } else if (error_code === noLeadUser) {
      setFullAttendeesList(firmUserRef?.current || []);
    }

    setErrorFixDialogData({
      show: true,
      ...rowData,
    });
  };

  /** --- onHideDialog hides the Dialog component for error fix --- **/
  const onHideDialog = () => {
    resetReactHookForm({});
    setErrorFixDialogData({
      show: false,
    });
    setContactData();
  };

  //Toast message and api fetching after error fix operation
  const onErrorFixSuccess = () => {
    let successMessage = 'Error Fixed Successfully';
    addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, successMessage);

    getAutomationErrorLogs(selectedSk, generateGetAutomationErrorLogsApiPayload(filterValues, last_updated_format));
    resetReactHookForm();
  };

  const onErrorFixFail = (errMsg = '') => {
    let errorMessage = 'Failed To Fix Error';
    if (errMsg) errorMessage = errMsg;

    addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMessage);
  };

  //===========================  ConfirmDialog component functions starts ==============================

  const onHideConfirmDialog = () => {
    setShowFixErrorConfirmDialog(false);
  };

  const handleConfirmErrorFix = async (e) => {
    setErrorFixDialogData({
      ...errorFixDialogData,
      show: false,
    });

    //Here we call all the error fix API api
    const { error_code, sk, pk, client_cognito_username, case_id, is_lead, contact_id, firm_user_id, lawft_id } = errorFixDialogData || {};

    let functionParams = {};

    functionParams.errorPk = pk;
    functionParams.errorSk = sk;

    if (error_code === userNotInCase) {
      functionParams = {
        ...functionParams,
        sk,
        pk,
        client_cognito_username,
        firm_user_id,
        case_id,
        is_lead,
        onErrorFixSuccess,
        onErrorFixFail,
      };
      handleUserNotInCaseError(functionParams);
    } else if ([noEmail, noLawftAccess, noPhoneNumber]?.includes(error_code)) {
      functionParams = {
        ...functionParams,
        sk,
        pk,
        contact_id,
        is_lead,
        email: getValues('email'),
        is_create_client_portal: getValues('lawftAccess'),
        error_code,
        contactData,
        automationErrorLogCodes,
        onErrorFixSuccess,
        onErrorFixFail,
        getValues,
      };

      handleUpdateErrorOccuredContactDetails(functionParams);
    } else if (error_code === noBillingContact) {
      functionParams = {
        ...functionParams,
        contact_id,
        is_lead,
        case_id,
        billingContacts: getValues('billingContacts'),
        onErrorFixSuccess,
        onErrorFixFail,
      };

      handleNoBillingContactError(functionParams);
    } else if ([noLeadAttorney, noLeadAssociate, noLeadParalegal, noLeadCaseManager]?.includes(error_code)) {
      functionParams = {
        ...functionParams,
        tenantId,
        tenantSk,
        pk,
        sk,
        is_lead,
        case_id,
        error_code,
        selectedTeamMember: getValues('teamMembers'),
        automationErrorLogCodes,
        teamMembersList,
        onErrorFixSuccess,
        onErrorFixFail,
      };

      handleNoLeadTeamMemberError(functionParams);
    } else if ([userArchived, userUnconfirmed, groupMembersArchived, groupMembersUnconfirmed].includes(error_code)) {
      functionParams = {
        ...functionParams,
        is_lead,
        case_id,
        error_code,
        selectedFirmUsers: getValues('firmUsers'),
        automationErrorLogCodes,
        fullAttendeesList,
        selectedAutomationRuleDetails,
        selectedAutomationErrorLogDetails,
        onErrorFixSuccess,
        onErrorFixFail,
      };
      handleUsersAutomationErrors(functionParams, tenantId, tenantSk, dispatch, addToast);
    } else if ([senderArchived, senderUnconfirmed].includes(error_code)) {
      functionParams = {
        ...functionParams,
        is_lead,
        case_id,
        error_code,
        selectedSender: getValues('senderUser'),
        automationErrorLogCodes,
        messageRecipientsList,
        selectedAutomationRuleDetails,
        selectedAutomationErrorLogDetails,
        onErrorFixSuccess,
        onErrorFixFail,
      };
      handleSenderAutomationErrors(functionParams);
    } else if (error_code === eventTimePassed) {
    } else if (error_code === noBusinessEmail) {
      const getMailDomain = () => {
        const environment = AppConfig.env;
        if (environment === 'dev') {
          return `@${userDetails.userDetails?.subdomain}.develop.lawft.com`;
        }
        if (environment === 'qa') {
          return `@${userDetails.userDetails?.subdomain}.qa.lawft.com`;
        }
        if (environment === 'staging') {
          return `@${userDetails.userDetails?.subdomain}.lawft.com`;
        }
        if (environment === 'prod') {
          return `@${userDetails.userDetails?.subdomain}.lawft.com`;
        }
      };
      const businessEmailDomain = getMailDomain();
      functionParams = {
        ...functionParams,
        is_lead,
        firm_user_id,
        error_code,
        businessEmail: getValues('businessEmail') + businessEmailDomain,
        automationErrorLogCodes,
        messageRecipientsList,
        selectedAutomationRuleDetails,
        selectedAutomationErrorLogDetails,
        onErrorFixSuccess,
        onErrorFixFail,
      };
      handleBusinessEmailAutomationErrors(functionParams);
    } else if (error_code === noLeadUser) {
      functionParams = {
        ...functionParams,
        is_lead,
        case_id,
        error_code,
        selectedTeamMember: getValues('leadAssignedUser'),
        onErrorFixFail,
        onErrorFixSuccess,
      };
      handleLeadUserAutomationErrors(functionParams);
    } else if (error_code === noLawftId) {
      functionParams = {
        ...functionParams,
        lawft_id,
        is_lead,
        case_id,
        error_code,
        contactData: getValues('contactData'),
        onErrorFixFail,
        onErrorFixSuccess,
      };
      handleLawftIdAutomationErrors(functionParams);
    }
  };

  //===========================  ConfirmDialog component functions ends ==============================

  /** --- memoized datatable-columns and data --- **/
  const memoizedAutomationRuleListColumns = useMemo(
    () =>
      generateAutomationErrorLogDataTableColumns({
        handleFixActionButtonClick,
        month_date_year_format,
        month_date_year_time_12_format,
        timeZone: userContext?.userDetails?.timezone,
        history,
      }),
    []
  );

  useEffect(() => {
    if (contactData?.email) {
      resetReactHookForm((formValues, keepStateOptions) => {
        return {
          ...formValues,
          lawftAccess: true,
          email: contactData?.email,
        };
      });
    }
  }, [contactData?.email]);

  useEffect(() => {
    if (selectedSk) getAutomationErrorLogs(selectedSk, generateGetAutomationErrorLogsApiPayload(filterValues, last_updated_format));
  }, [selectedSk]);

  useEffect(() => {
    clearLocalStorageItems(updateAutomationRuleLocalStorageItemsList);
    loadTotalCount();

    setAppTitle('Automation', 'Error Log');

    // clean up function
    return () => {
      setAppTitle();
    };
  }, []);

  useEffect(() => {
    if (firmUsersList?.length) firmUserRef.current = firmUsersList;
  }, [firmUsersList]);

  // // useEffect to handle isAutomationErrorLogScreen state
  // useEffect(() => {
  //   setIsAutomationErrorLogScreen(true); // Set isAutomationErrorLogScreen to true when component mounts

  //   return () => {
  //     setIsAutomationErrorLogScreen(false); // Reset isAutomationErrorLogScreen to false when component unmounts
  //   };
  // }, [setIsAutomationErrorLogScreen]);

  useEffect(() => {
    if (selectedPk && selectedSk) {
      getWorkFlowAutomationRuleDetails(selectedPk, selectedSk)
        .then((res) => {
          setSelectedAutomationRuleDetails(res?.data);
        })
        .catch((err) => {
          clearLocalStorageItems(updateAutomationRuleLocalStorageItemsList);
          console.log('err', err);
        });
    } else {
      clearLocalStorageItems(updateAutomationRuleLocalStorageItemsList);
    }
  }, [selectedPk, selectedSk]);

  return (
    <div className="container-fluid px-0">
      <div className="d-flex align-items-center m-1">
        <div className="col-md-8 col-8">
          <div>
            <AutomationErrorLogHeading history={history} />

            <div className="col-lg-12">
              {automationErrorLogsDetails ? <AutomationErrorLogDetails automationErrorLogsDetails={automationErrorLogsDetails} /> : null}
            </div>

            <div className="font-italic mb-3 mt-2">Total Automation Error Logs : {loading ? 'Loading...' : totalCount}</div>
          </div>
        </div>
      </div>

      {/* filter section */}
      <div className="d-flex flex-wrap align-items-center mb-3 p-1">
        <AutomationErrorLogFilters
          onChangeFilterFacets={memoHandleFilterValueChange}
          onResetFilterFacets={resetFilters}
          filterValues={filterValues}
          automationErrorLogStatusOptions={automationErrorLogStatusOptions}
          automationErrorLogCodeOptions={automationErrorLogCodeOptions}
        />
      </div>

      {/* data-table section */}
      <div className="col-sm-12 overflow-auto datatable-wrapper">
        <PrimeReactDataTable
          tableValues={automationErrorLogsList}
          columnFields={memoizedAutomationRuleListColumns}
          isPaginated={true}
          paginationData={paginationData}
          storageKey="column_widths_automation_error_logs"
          onNextPageClick={(e) =>
            handlePagination({
              ...getHandlePaginationCommonParms(),
              type: 'next',
            })
          }
          onPrevPageClick={(e) =>
            handlePagination({
              ...getHandlePaginationCommonParms(),
              type: 'prev',
            })
          }
          onPageChange={(e) => {
            handlePagination({
              ...getHandlePaginationCommonParms(),
              setPaginationData,
              page_limit: e?.value || 10,
              type: '',
            });
            handlePageNumberInPagination(userDetails, 'automation', '', 'error_list', e?.value);
          }}
          stripedRows
          resizableColumns
        />
      </div>

      {errorFixDialogData?.show ? (
        <PrimeReactDialog
          className="new-task-popup error-log-fix-popup"
          header={customHeaderTemplate}
          isVisible={errorFixDialogData?.show}
          style={{ width: '40vw' }}
          footer={renderFooter}
          onHideDialog={onHideDialog}
        >
          <DynamicErrorLogForm
            reactHookForm={reactHookForm}
            errorFixDialogData={errorFixDialogData}
            onHideDialog={onHideDialog}
            billingContactsOptions={billingContactsOptions}
            teamMembersList={teamMembersList}
            fullAttendeesList={fullAttendeesList}
            messageRecipientsList={getMessageSendersList(messageRecipientsList)}
            triggerName={triggerName}
            setShowFixErrorConfirmDialog={setShowFixErrorConfirmDialog}
          />
        </PrimeReactDialog>
      ) : null}

      {showFixErrorConfirmDialog ? (
        <PrimeReactConfirmDialog
          className="fix-confirmation-popup"
          visible={showFixErrorConfirmDialog}
          message={generateFixErrorConfirmDialogMessage({
            errorFixDialogData,
            automationErrorLogCodes,
            getValues,
          })}
          header="Confirm Fix Error"
          // icon="pi pi-exclamation-triangle"
          onHideConfirmDialog={() => onHideConfirmDialog(false)}
          onAcceptConfirmDialog={handleConfirmErrorFix}
          OnRejectConfirmDialog={() => onHideConfirmDialog(false)}
          style={{ width: '40vw' }}
          closable={true}
        />
      ) : null}
    </div>
  );
}

const ListAutomationErrorLogWrapper = () => {
  return (
    <AutomationContextProvider>
      <GlobalLoader />
      <ListAutomationErrorLog />
    </AutomationContextProvider>
  );
};
export default ListAutomationErrorLogWrapper;
